import { memo } from 'react';
import type { CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { LineChart, Line } from 'recharts';
import styles from './ReadinessView.module.scss';
import { TeamReadinessAthlete, InjuredAthleteSummary } from '../types';
import { formatFullName } from '../../utils/functions';
import { formatJump } from '../utils/jumpUnit';
import { InjuryBadge } from '../../shared/InjuryBadge/InjuryBadge';
import { ComplaintBadge, ComplaintDetail } from '../../shared/ComplaintBadge/ComplaintBadge';
import { INJURY_BODY_ZONE, INJURY_SEVERITY } from '../../enums/injuries';
import { HooperMetric } from '../../components/DetailTeamPage/SubjectReadinessTab/HooperHistoryModal';
import { MetricKey } from './MetricHistoryModal/metricHistory.constants';
import { CONFIDENCE_BADGES, PHASE_BADGES } from '../constants/readiness';
import {
  getDomainColor,
  getMentalValueColor,
  getDOMSValueColor,
  getACWRValueColor,
  getSRPEValueColor,
} from './readinessColors';

interface ReadinessMatrixRowProps {
  item: TeamReadinessAthlete;
  complaintsByAthlete: Record<string, ComplaintDetail[]>;
  injuredMap: Record<string, InjuredAthleteSummary>;
  onHooperCellClick: (metric: HooperMetric, item: TeamReadinessAthlete) => void;
  onMetricCellClick: (metric: MetricKey, item: TeamReadinessAthlete) => void;
  /** Click sulla colonna sRPE → naviga alla tab Sforzo */
  onGoToEffort?: () => void;
}

/**
 * Riga della matrice Readiness per un singolo atleta: RI + 4 gruppi di
 * dominio (mentale/muscolare/cardio/strain) + trend. Estratta da
 * `TeamReadinessTab` per contenerne le dimensioni (regola ≤600 righe).
 */
function ReadinessMatrixRow({
  item,
  complaintsByAthlete,
  injuredMap,
  onHooperCellClick,
  onMetricCellClick,
  onGoToEffort,
}: ReadinessMatrixRowProps) {
  const ri = item.ri;
  const hooper = item.metrics?.hooper;
  const cmjVal = item.metrics?.cmj; // number | null
  const fcrVal = item.metrics?.fcr; // number | null
  const hrvVal = item.metrics?.hrv; // number | null
  const sRPEVal = item.metrics?.sRPE; // number | null
  const fullName = formatFullName(item.firstName, item.lastName);

  return (
    <tr className={styles.tableRow}>
      <td className={styles.tdNameSticky}>
        <div className={styles.nameCell}>
          <Link to={`/dashboard/users/${item.athleteId}`} className={styles.athleteLink}>
            {fullName}
          </Link>
          {ri?.status === 'sotto_stress' && (
            <div className={styles.alertWrapper}>
              <AlertCircle className={styles.alertIcon} />
              <div className={styles.alertTooltip}>
                <div className={styles.alertTooltipInner}>
                  <p className={styles.alertTooltipTitle}>Stato critico - Ridurre carico</p>
                  <p className={styles.alertTooltipDesc}>
                    RI: {ri.value}
                  </p>
                  <div className={styles.alertTooltipArrow}></div>
                </div>
              </div>
            </div>
          )}
          {item.hasComplaints && (
            <ComplaintBadge
              complaints={complaintsByAthlete[item.athleteId]}
              maxIntensity={item.complaintMaxIntensity}
              size="sm"
            />
          )}
          {injuredMap[item.athleteId] && (
            <InjuryBadge
              size="sm"
              injury={{
                injuryId: injuredMap[item.athleteId].injuryId,
                athleteId: item.athleteId,
                bodyZone: injuredMap[item.athleteId].bodyZone as INJURY_BODY_ZONE,
                daysOut: injuredMap[item.athleteId].daysOut,
                severity: injuredMap[item.athleteId].severity as INJURY_SEVERITY,
                rtpPhase: injuredMap[item.athleteId].rtpPhase,
                expectedReturnDate: injuredMap[item.athleteId].expectedReturnDate,
              }}
            />
          )}
        </div>
      </td>

      <td
        className={`${styles.tdRiCenter} ${ri ? styles.tdClickable : ''}`}
        onClick={() => ri && onMetricCellClick('ri', item)}
      >
        {ri ? (
          <div className={styles.flexColCenterGap2}>
            <p
              className={`${styles.riValue} ${styles.riValueDynamic}`}
              style={{ '--metric-color': getDomainColor(ri.value) } as CSSProperties}
            >
              {ri.value}
            </p>
            <div className={styles.badgeRow}>
              {ri.confidenceBadge && CONFIDENCE_BADGES[ri.confidenceBadge] && (
                <span
                  title={CONFIDENCE_BADGES[ri.confidenceBadge].hint}
                  className={`${styles.riBadge} ${styles.riBadgeHint}`}
                  style={{
                    '--badge-bg': CONFIDENCE_BADGES[ri.confidenceBadge].bg,
                    '--badge-fg': CONFIDENCE_BADGES[ri.confidenceBadge].color,
                  } as CSSProperties}
                >
                  {CONFIDENCE_BADGES[ri.confidenceBadge].short}
                </span>
              )}
              {ri.calibrationBadge && (
                <span
                  className={styles.riBadge}
                  style={{
                    '--badge-bg': PHASE_BADGES[1].bg,
                    '--badge-fg': PHASE_BADGES[1].color,
                  } as CSSProperties}
                >
                  CAL
                </span>
              )}
            </div>
          </div>
        ) : (
          <p className={`${styles.riValue} ${styles.textMuted}`}>-</p>
        )}
      </td>

      {/* Mental: Sonno — qualità 1-5 Hooper, con accanto le ore dormite.
          "3" da solo non dice se ha dormito male in cinque ore o male in
          nove: sono due problemi diversi, e le ore erano leggibili solo
          passando col mouse una cella per volta. */}
      <td className={styles.tdClickable} onClick={() => hooper?.sleepQuality != null && onHooperCellClick('sleepQuality', item)}>
        <div className={styles.cellValueGroup}>
          <p className={`${styles.cellValueText} ${hooper?.sleepQuality != null ? styles.cellValueTextFilled : styles.textMuted}`}>
            {hooper?.sleepQuality ?? '-'}
            {hooper?.sleepHours != null && (
              <span className={styles.cellValueHours}>
                ({hooper.sleepHours.toLocaleString('it-IT', { maximumFractionDigits: 1 })}h)
              </span>
            )}
          </p>
          {hooper?.sleepQuality != null && (
            <div
              className={`${styles.cellDot} ${styles.cellDotDynamic}`}
              style={{ '--metric-color': getMentalValueColor(hooper.sleepQuality) } as CSSProperties}
            />
          )}
          {hooper?.sleepHours != null && (
            <div className={styles.cardTooltip}>
              <div className={styles.cardTooltipInner}>
                <p className={styles.cardTooltipTitle}>Sonno</p>
                <p className={styles.cardTooltipDesc}>Qualità: {hooper.sleepQuality ?? '-'}/5 · Ore: {hooper.sleepHours}h</p>
                <div className={styles.cardTooltipArrow}></div>
              </div>
            </div>
          )}
        </div>
      </td>

      {/* Mental: Stress */}
      <td className={styles.tdClickable} onClick={() => hooper?.stress != null && onHooperCellClick('stressLevel', item)}>
        <div className={styles.cellValueGroup}>
          <p className={`${styles.cellValueText} ${hooper?.stress != null ? styles.cellValueTextFilled : styles.textMuted}`}>
            {hooper?.stress ?? '-'}
          </p>
          {hooper?.stress != null && (
            <div
              className={`${styles.cellDot} ${styles.cellDotDynamic}`}
              style={{ '--metric-color': getMentalValueColor(hooper.stress) } as CSSProperties}
            />
          )}
        </div>
      </td>

      {/* Mental: Umore */}
      <td className={styles.tdClickableBorderR} onClick={() => hooper?.mood != null && onHooperCellClick('mood', item)}>
        <div className={styles.cellValueGroup}>
          <p className={`${styles.cellValueText} ${hooper?.mood != null ? styles.cellValueTextFilled : styles.textMuted}`}>
            {hooper?.mood ?? '-'}
          </p>
          {hooper?.mood != null && (
            <div
              className={`${styles.cellDot} ${styles.cellDotDynamic}`}
              style={{ '--metric-color': getMentalValueColor(hooper.mood) } as CSSProperties}
            />
          )}
        </div>
      </td>

      {/* Muscular: CMJ */}
      <td
        className={styles.tdClickable}
        onClick={() => cmjVal != null && onMetricCellClick('cmj', item)}
      >
        <div className={styles.cellValueGroup}>
          <p className={`${styles.cellValueText} ${cmjVal != null ? styles.cellValueTextFilled : styles.textMuted}`}>
            {cmjVal != null ? formatJump(cmjVal, item.metrics?.cmjUnit) : '-'}
          </p>
        </div>
      </td>

      {/* Muscular: DOMS */}
      <td className={styles.tdClickableBorderR} onClick={() => hooper?.doms != null && onHooperCellClick('muscleSoreness', item)}>
        <div className={styles.cellValueGroup}>
          <p className={`${styles.cellValueText} ${hooper?.doms != null ? styles.cellValueTextFilled : styles.textMuted}`}>
            {hooper?.doms ?? '-'}
          </p>
          {hooper?.doms != null && (
            <div
              className={`${styles.cellDot} ${styles.cellDotDynamic}`}
              style={{ '--metric-color': getDOMSValueColor(hooper.doms) } as CSSProperties}
            />
          )}
        </div>
      </td>

      {/* Cardio: HRrest */}
      <td
        className={styles.tdClickable}
        onClick={() => fcrVal != null && onMetricCellClick('fcr', item)}
      >
        <div className={styles.cellValueGroup}>
          <p className={`${styles.cellValueText} ${fcrVal != null ? styles.cellValueTextFilled : styles.textMuted}`}>
            {fcrVal ?? '-'}
          </p>
        </div>
      </td>

      {/* Cardio: HRV */}
      <td
        className={styles.tdClickableBorderR}
        onClick={() => hrvVal != null && onMetricCellClick('hrv', item)}
      >
        <div className={styles.cellValueGroup}>
          <p className={`${styles.cellValueText} ${hrvVal != null ? styles.cellValueTextFilled : styles.textMuted}`}>
            {hrvVal != null ? hrvVal.toFixed(0) : '-'}
          </p>
        </div>
      </td>

      {/* Strain: sRPE — click → tab Sforzo */}
      <td
        className={styles.tdClickableStrainBorderL}
        onClick={onGoToEffort}
        title="Vai alla tab Sforzo"
      >
        <p
          className={`${styles.sRPEValue} ${styles.sRPEValueDynamic}`}
          style={{ '--metric-color': sRPEVal != null ? getSRPEValueColor(sRPEVal) : '#b3b3b3' } as CSSProperties}
        >
          {sRPEVal != null ? Math.round(sRPEVal) : '-'}
        </p>
      </td>

      {/* Strain: ACWR */}
      <td className={styles.tdClickable}>
        <div className={styles.cellValueGroup}>
          <p
            className={`${styles.cellValueTextSm} ${styles.cellValueTextSmDynamic}`}
            style={{ '--metric-color': item.strain?.acwr != null ? getACWRValueColor(item.strain.acwr) : '#b3b3b3' } as CSSProperties}
          >
            {item.strain?.acwr != null ? item.strain.acwr.toFixed(2) : '-'}
          </p>
        </div>
      </td>

      {/* Trend: 7-day sparkline */}
      <td className={styles.tdClickable}>
        {item.sparkline && item.sparkline.some((v: any) => v != null) ? (
          <LineChart width={80} height={24} data={item.sparkline.map((v: any, i: number) => ({ i, v }))}>
            <Line type="monotone" dataKey="v" stroke="#063444" strokeWidth={1.5} dot={false} connectNulls={false} />
          </LineChart>
        ) : (
          <span className={styles.sparklineEmpty}>-</span>
        )}
      </td>
    </tr>
  );
}

export default memo(ReadinessMatrixRow);
